import type {
  NodeChangeStatus,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { dropdownMenu } from "@repo/ui/Dropdown/dropdownMenuStyles";
import { IconFolder, IconPlus } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import {
  Menu as AriaMenu,
  MenuItem,
  MenuTrigger,
  Popover,
  TreeItem,
  TreeItemContent,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { treeItem } from "../tree/treeStyles.js";
import { serializeAssessmentTreeItemKey } from "./assessmentTreeHelpers.js";
import { NodeChangeDot } from "./nodeChangeDot.js";

export interface AssessmentSectionTreeItemProps {
  section: PlaybookSectionNode;
  sectionIndex: number;
  level?: number;
  isLastItemAtLevel?: boolean;
  onAction?: () => void;
  className?: string;
  children?: ReactNode;
  onAddCriteria?: (sectionId: string) => void;
  onAddSubSection?: (sectionId: string) => void;
  isReadOnly?: boolean;
  /** The change status of this node compared to the live version */
  changeStatus?: NodeChangeStatus;
  /** Show the agent-suggestion indicator dot. */
  hasSuggestion?: boolean;
  /**
   * When true this is a `new_section` proposal (not yet in the tree): uses the
   * `ghost-section` tree key, renders the proposal name with a placeholder,
   * and hides the add-to-section menu.
   */
  isGhost?: boolean;
}

/** Renders a section tree item in the assessment tree UI. */
export const AssessmentSectionTreeItem: FC<AssessmentSectionTreeItemProps> = ({
  section,
  sectionIndex,
  level = 0,
  isLastItemAtLevel = false,
  onAction,
  className,
  children,
  onAddCriteria,
  onAddSubSection,
  isReadOnly,
  changeStatus,
  hasSuggestion = false,
  isGhost = false,
}) => {
  const treeItemId = serializeAssessmentTreeItemKey({
    type: isGhost ? "ghost-section" : "section",
    id: section.id,
  });
  const textValue = section.name || `Section ${sectionIndex + 1}`;

  // Calculate interior padding based on hierarchy level
  const getInteriorPaddingClass = () => {
    switch (level) {
      case 0:
        return "px-4";
      case 1:
        return "pl-6 pr-4";
      case 2:
        return "pl-12 pr-4";
      case 3:
        return "pl-16 pr-4";
      default:
        return "pl-20 pr-4";
    }
  };

  return (
    <TreeItem
      id={treeItemId}
      textValue={textValue}
      onAction={onAction}
      className={treeItem({
        className,
        isLastItemAtLevel,
      })}
    >
      <TreeItemContent>
        <div
          className={twMerge(
            "flex items-center space-x-4 py-2",
            getInteriorPaddingClass(),
          )}
        >
          {/* Hierarchy indicator */}
          {level > 0 && (
            <div className="h-3 w-3 shrink-0 rounded-bl-md border-b-2 border-l-2 border-border-strong" />
          )}

          {/* Icon */}
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gray-100">
            <IconFolder className="h-4 w-4 text-sidebar-foreground" />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            <div className="truncate leading-6 font-semibold text-foreground">
              {section.name ||
                (isGhost ? (
                  <span className="text-muted-foreground">
                    Proposed section
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    Section {sectionIndex + 1}
                  </span>
                ))}
            </div>
            {section.description && (
              <div className="truncate text-xs text-muted-foreground">
                {section.description}
              </div>
            )}
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4 text-xs">
            {!isReadOnly && !isGhost && (onAddCriteria || onAddSubSection) && (
              <MenuTrigger>
                <IconButton
                  accessibleLabel="Add to this section"
                  icon={<IconPlus className="size-4 shrink-0" />}
                  variant={ButtonVariant.Secondary}
                />

                <Popover
                  placement="bottom end"
                  className={dropdownMenu().popover()}
                >
                  <AriaMenu className={dropdownMenu().menu()}>
                    {onAddCriteria ? (
                      <MenuItem
                        className={dropdownMenu().item()}
                        onAction={() => onAddCriteria(section.id)}
                      >
                        Add criteria
                      </MenuItem>
                    ) : null}

                    {onAddSubSection ? (
                      <MenuItem
                        className={dropdownMenu().item()}
                        onAction={() => onAddSubSection(section.id)}
                      >
                        Add sub-section
                      </MenuItem>
                    ) : null}
                  </AriaMenu>
                </Popover>
              </MenuTrigger>
            )}
            <NodeChangeDot
              entity="section"
              variant={hasSuggestion ? "suggestion" : "draft"}
              isVisible={hasSuggestion || changeStatus !== undefined}
            />
          </div>
        </div>
      </TreeItemContent>
      {children}
    </TreeItem>
  );
};
